/* ---------------------------------------------------------------------
 *                            Import Packages
 * --------------------------------------------------------------------- */

// React
import React, { useEffect, useRef } from "react";

// MUI
import { Box, Avatar, CircularProgress } from "@mui/material";

// Images
import robot from "../../assets/images/logo1.png";

/* ---------------------------------------------------------------------
 *                         ChatContainer Component
 * --------------------------------------------------------------------- */

/**
 * 定义AI对话区
 * @param {Array} param.chatMessages - 对话信息数据
 * @param {boolean} param.isLoading - 是否正在加载AI回复
 * @function ChatContainer
 * @returns AI对话区组件
 */
export default function ChatContainer({ chatMessages, isLoading = false }) {
  /**
   * 创建一个 ref 用于引用消息列表底部的元素
   * @type {React.RefObject<HTMLDivElement>}
   */
  const messagesEndRef = useRef(null);

  /**
   * 滚动到消息列表底部
   * @returns {void}
   */
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  /**
   * 监听消息列表变化，自动滚动到底部
   * @returns {void}
   */
  useEffect(() => {
    scrollToBottom();
  }, [chatMessages, isLoading]);

  /* ---------------------------------------------------------------------
   *                            Render Component
   * --------------------------------------------------------------------- */
  return (
    <Box
      sx={{
        flex: 1,
        overflowY: "auto",
        padding: "16px",
        paddingTop: "50px",
        display: "flex",
        flexDirection: "column",
        gap: 2,
      }}
    >
      {chatMessages.map((message) => (
        <Box
          key={message.id}
          sx={{
            display: "flex",
            alignItems: "flex-start",
            gap: 1,
            flexDirection: message.sender === "user" ? "row-reverse" : "row",
          }}
        >
          {/* 头像 */}
          {message.sender === "user" ? (
            <Avatar
              sx={{
                width: 37,
                height: 37,
                bgcolor: "primary.main",
              }}
            >
              U
            </Avatar>
          ) : (
            <img
              src={robot}
              alt="AI"
              style={{ width: 37, height: 37, borderRadius: "50%" }}
            />
          )}

          {/* 消息气泡 */}
          <Box
            sx={{
              maxWidth: "70%",
              padding: "10px 14px",
              borderRadius: "12px",
              backgroundColor:
                message.sender === "user" ? "primary.main" : "grey.100",
              color: message.sender === "user" ? "white" : "text.primary",
              fontSize: "14px",
              lineHeight: 1.5,
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              boxShadow: 1,
              position: "relative",
              "&::before": {
                content: '""',
                position: "absolute",
                top: "12px",
                [message.sender === "user" ? "right" : "left"]: "-6px",
                width: "12px",
                height: "12px",
                backgroundColor:
                  message.sender === "user" ? "primary.main" : "grey.100",
                transform: "rotate(45deg)",
                zIndex: -1,
              },
            }}
          >
            {message.content}
          </Box>
        </Box>
      ))}

      {/* 正在思考的加载气泡 */}
      {isLoading && (
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            gap: 1,
            flexDirection: "row",
          }}
        >
          {/* AI头像 */}
          <img
            src={robot}
            alt="AI"
            style={{ width: 37, height: 37, borderRadius: "50%" }}
          />

          {/* 思考气泡 */}
          <Box
            sx={{
              maxWidth: "70%",
              padding: "12px 16px",
              borderRadius: "12px",
              backgroundColor: "grey.100",
              color: "text.primary",
              fontSize: "14px",
              lineHeight: 1.5,
              boxShadow: 1,
              position: "relative",
              display: "flex",
              alignItems: "center",
              gap: 1,
              "&::before": {
                content: '""',
                position: "absolute",
                top: "12px",
                left: "-6px",
                width: "12px",
                height: "12px",
                backgroundColor: "grey.100",
                transform: "rotate(45deg)",
                zIndex: -1,
              },
            }}
          >
            {/* 加载动画 */}
            <CircularProgress size={16} />
            <span>AI正在思考中...</span>
          </Box>
        </Box>
      )}

      {/* 添加一个空的 div 作为滚动目标 */}
      <div ref={messagesEndRef} />
    </Box>
  );
}
